import React, { useMemo } from 'react';
import { useInvoicePayments, InvoicePayment } from '../../client-invoice-payments/useData';
import { useTheme } from '../../../contexts/ThemeContext';

export interface PaymentTrendsChartProps {
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
  months?: number;
}

const PaymentTrendsChart: React.FC<PaymentTrendsChartProps> = ({
  embedded,
  settings,
  months = 6,
}) => {
  const effectiveMonths = (settings?.months as number) ?? months;
  const { invoicePayments = [], isLoading } = useInvoicePayments();
  const { colors } = useTheme();

  const chartData = useMemo(() => {
    const arr = Array.isArray(invoicePayments) ? invoicePayments : [];
    const now = new Date();
    const byMonth: Record<string, number> = {};
    for (let i = effectiveMonths - 1; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      byMonth[key] = 0;
    }
    arr.forEach((p: InvoicePayment) => {
      const dateStr = p.paymentDate?.slice(0, 7);
      if (dateStr && byMonth[dateStr] !== undefined) {
        byMonth[dateStr] += p.paymentAmount || 0;
      }
    });
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, amount]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', {
          month: 'short',
          year: '2-digit',
        }),
        amount,
      }));
  }, [invoicePayments, effectiveMonths]);

  const cardClass = embedded
    ? 'min-w-0'
    : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Payment Trends
        </h2>
        <div className="h-48 flex items-center justify-center">
          <div className={`animate-pulse ${colors.textMuted}`}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!chartData.length) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
          Payment Trends
        </h2>
        <div className="h-48 flex items-center justify-center">
          <p className={colors.textMuted}>No payment data</p>
        </div>
      </div>
    );
  }

  const maxAmount = Math.max(...chartData.map((d) => d.amount), 1);
  const barHeight = 120;
  const padding = 32;

  return (
    <div className={cardClass}>
      <h2 className={`text-base font-semibold ${colors.text} mb-4`}>
        Payment Trends
      </h2>
      <div className="flex flex-col gap-2">
        {chartData.map((d, i) => (
          <div key={d.month} className="flex items-center gap-3">
            <span className={`text-xs w-12 flex-shrink-0 ${colors.textMuted}`}>
              {d.month}
            </span>
            <div className="flex-1 flex items-center gap-2">
              <div
                className="h-6 rounded bg-teal-500 dark:bg-teal-600 flex-shrink-0 transition-all"
                style={{
                  width: `${Math.max(4, (d.amount / maxAmount) * 100)}%`,
                  minWidth: d.amount > 0 ? 8 : 0,
                }}
              />
              <span className={`text-xs ${colors.text} flex-shrink-0`}>
                €{d.amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaymentTrendsChart;
